import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  FiChevronLeft, FiChevronRight, FiCalendar, FiUsers, FiPlus, 
  FiX, FiUser, FiPhone, FiMail, FiMapPin, FiClock, FiTrash2,
  FiCheck, FiGrid, FiList, FiLock, FiEdit2
} from 'react-icons/fi';
import { 
  addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, 
  format, isSameDay, isSameMonth, parseISO, addWeeks, subWeeks,
  addMonths, subMonths, eachDayOfInterval, getDay, setHours, setMinutes
} from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Heures de travail - 8h à minuit (type Google Agenda)
const WORK_HOURS = Array.from({ length: 17 }, (_, i) => i + 8); // 8h à 00h (8, 9, ... 23, 24)

export default function PlanningPage() {
  const { isCollaborator } = useAuth();
  const [planning, setPlanning] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week', 'month', or 'day'
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(null); // { date, hour, equipmentId }
  const [showBlockModal, setShowBlockModal] = useState(null);
  const [newMember, setNewMember] = useState({ name: '', email: '', phone: '', role: 'Technicien' });
  const [selectedEquipment, setSelectedEquipment] = useState('all');

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadPlanning();
  }, [currentDate, viewMode]);

  const loadPlanning = async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      
      if (viewMode === 'week' || viewMode === 'day') {
        startDate = viewMode === 'day' ? currentDate : startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = viewMode === 'day' ? currentDate : endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      }
      
      const response = await fetch(
        `${API_URL}/api/planning?start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setPlanning(data.planning || []);
        setTeamMembers(data.team_members || []);
        setBlockedSlots(data.blocked_slots || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  };

  // Créer une réservation rapide
  const handleCreateReservation = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/quick`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Réservation créée');
        setShowCreateModal(null);
        loadPlanning();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Bloquer un créneau
  const handleBlockSlot = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/planning/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Créneau bloqué');
        setShowBlockModal(null);
        loadPlanning();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du blocage');
      }
    } catch (error) {
      toast.error('Erreur lors du blocage');
    }
  };

  // Débloquer un créneau
  const handleUnblockSlot = async (slotId) => {
    try {
      const response = await fetch(`${API_URL}/api/planning/block/${slotId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Créneau débloqué');
        loadPlanning();
      }
    } catch (error) {
      toast.error('Erreur lors du déblocage');
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTeamMembers(await response.json());
      }
    } catch (error) {
      console.error('Error loading team:', error);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMember.name) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/team-members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newMember)
      });

      if (response.ok) {
        toast.success('Membre ajouté');
        setNewMember({ name: '', email: '', phone: '', role: 'Technicien' });
        setShowAddMember(false);
        loadTeamMembers();
        loadPlanning();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm('Supprimer ce membre de l\'équipe ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/team-members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Membre supprimé');
        loadTeamMembers();
        loadPlanning();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAssignTeam = async (reservationId, memberIds) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/assign-team`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ team_member_ids: memberIds })
      });

      if (response.ok) {
        toast.success('Équipe assignée');
        loadPlanning();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  // Navigation
  const goToPrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, -1));
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  // Get pending requests for the top banner
  const pendingRequests = planning.flatMap(item => 
    (item.reservations || []).filter(r => r.status === 'pending')
  ).sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

  // Validate a reservation
  const handleValidateReservation = async (reservationId, action) => {
    try {
      const newStatus = action === 'approve' ? 'confirmed' : 'rejected';
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(action === 'approve' ? 'Réservation acceptée !' : 'Réservation refusée');
        loadPlanning();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Get days based on view mode
  const getDays = () => {
    if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    } else {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const startDay = startOfWeek(monthStart, { weekStartsOn: 1 });
      const endDay = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: startDay, end: endDay });
    }
  };

  const days = getDays();

  const getReservationsForDay = (equipmentId, day) => {
    const item = planning.find(p => p.equipment?.id === equipmentId);
    if (!item) return [];

    return item.reservations.filter(r => {
      const startDate = parseISO(r.start_date);
      const endDate = r.end_date ? parseISO(r.end_date) : startDate;
      return isSameDay(day, startDate) || isSameDay(day, endDate) || 
             (day >= startDate && day <= endDate);
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'confirmed':
        return 'bg-emerald-500';
      case 'pending':
        return 'bg-amber-500';
      case 'completed':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Confirmé';
      case 'confirmed': return 'Confirmé';
      case 'pending': return 'En attente';
      case 'completed': return 'Terminé';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-page">
      {/* Pending Requests Banner */}
      {pendingRequests.length > 0 && (
        <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                <h3 className="font-semibold text-orange-800">
                  {pendingRequests.length} demande{pendingRequests.length > 1 ? 's' : ''} en attente de validation
                </h3>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {pendingRequests.slice(0, 5).map((request, i) => (
                <div 
                  key={request.id || i}
                  onClick={() => setSelectedReservation(request)}
                  className="flex-shrink-0 bg-white rounded-lg border border-orange-200 p-3 min-w-[250px] cursor-pointer hover:shadow-md transition-shadow hover:border-orange-300"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-800">{request.client_name}</span>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">En attente</span>
                  </div>
                  <div className="text-xs text-slate-500 space-y-1">
                    <div className="flex items-center gap-1">
                      <FiCalendar className="h-3 w-3" />
                      {format(parseISO(request.event_date), 'dd/MM/yyyy', { locale: fr })}
                      {request.event_time && ` à ${request.event_time}`}
                    </div>
                    <div className="flex items-center gap-1">
                      <FiMapPin className="h-3 w-3" />
                      {request.event_type}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleValidateReservation(request.id, 'approve'); }}
                      className="flex-1 px-2 py-1.5 text-xs font-medium bg-emerald-500 text-white rounded hover:bg-emerald-600 flex items-center justify-center gap-1"
                    >
                      <FiCheck className="h-3 w-3" /> Valider
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleValidateReservation(request.id, 'reject'); }}
                      className="flex-1 px-2 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center gap-1"
                    >
                      <FiX className="h-3 w-3" /> Refuser
                    </button>
                  </div>
                </div>
              ))}
              {pendingRequests.length > 5 && (
                <div className="flex-shrink-0 flex items-center justify-center min-w-[100px] text-orange-600 text-sm">
                  +{pendingRequests.length - 5} autres
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Planning</h1>
          <p className="text-slate-600 mt-1">Vue des disponibilités par équipement</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'day' 
                  ? 'bg-white shadow text-indigo-600' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-day-btn"
            >
              <FiClock className="inline mr-1.5 h-4 w-4" />
              Jour
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'week' 
                  ? 'bg-white shadow text-indigo-600' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-week-btn"
            >
              <FiList className="inline mr-1.5 h-4 w-4" />
              Semaine
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'month' 
                  ? 'bg-white shadow text-indigo-600' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-month-btn"
            >
              <FiGrid className="inline mr-1.5 h-4 w-4" />
              Mois
            </button>
          </div>
          
          <Button variant="outline" onClick={() => setShowTeamModal(true)} data-testid="team-btn">
            <FiUsers className="mr-2 h-4 w-4" />
            Équipe ({teamMembers.length})
          </Button>
          
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <Button variant="ghost" size="icon" onClick={goToPrevious} data-testid="prev-btn">
              <FiChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" onClick={goToToday} className="px-3" data-testid="today-btn">
              Aujourd'hui
            </Button>
            <Button variant="ghost" size="icon" onClick={goToNext} data-testid="next-btn">
              <FiChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Planning Grid */}
      <Card className="border-slate-200 overflow-hidden shadow-sm">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 py-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <FiCalendar className="h-5 w-5 text-indigo-600" />
            {viewMode === 'week' 
              ? `Semaine du ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMMM yyyy', { locale: fr })}`
              : format(currentDate, 'MMMM yyyy', { locale: fr })
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {viewMode === 'day' ? (
            <DayView 
              currentDate={currentDate}
              planning={planning}
              blockedSlots={blockedSlots}
              getReservationsForDay={getReservationsForDay}
              getStatusColor={getStatusColor}
              setSelectedReservation={setSelectedReservation}
              onCellClick={isCollaborator ? null : (date, hour, equipmentId) => setShowCreateModal({ date, hour, equipmentId })}
              onBlockClick={isCollaborator ? null : (date, hour, equipmentId) => setShowBlockModal({ date, hour, equipmentId })}
              selectedEquipment={selectedEquipment}
            />
          ) : viewMode === 'week' ? (
            <WeekView 
              days={days}
              planning={planning}
              blockedSlots={blockedSlots}
              getReservationsForDay={getReservationsForDay}
              getStatusColor={getStatusColor}
              setSelectedReservation={setSelectedReservation}
              onCellClick={isCollaborator ? null : (date, equipmentId) => setShowCreateModal({ date, hour: 9, equipmentId })}
            />
          ) : (
            <MonthView 
              days={days}
              currentDate={currentDate}
              planning={planning}
              getReservationsForDay={getReservationsForDay}
              getStatusColor={getStatusColor}
              setSelectedReservation={setSelectedReservation}
            />
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-300" />
              <span className="text-slate-600">Disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-slate-600">En attente</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-slate-600">Confirmé</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-slate-600">Terminé</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="text-slate-600">Bloqué</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Equipment Filter for Day View */}
      {viewMode === 'day' && planning.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-slate-700">Filtrer par équipement :</span>
              <button
                onClick={() => setSelectedEquipment('all')}
                className={`px-3 py-1 rounded-full text-sm ${selectedEquipment === 'all' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                Tous
              </button>
              {planning.map(item => {
                const equipment = item.equipment || item.product;
                return (
                  <button
                    key={equipment?.id}
                    onClick={() => setSelectedEquipment(equipment?.id)}
                    className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${selectedEquipment === equipment?.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: equipment?.color || '#666' }} />
                    {equipment?.name}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reservation Detail Modal */}
      <AnimatePresence>
        {selectedReservation && (
          <ReservationModal
            reservation={selectedReservation}
            teamMembers={teamMembers}
            getStatusColor={getStatusColor}
            getStatusLabel={getStatusLabel}
            onAssignTeam={handleAssignTeam}
            onClose={() => setSelectedReservation(null)}
          />
        )}
      </AnimatePresence>

      {/* Team Management Modal */}
      <AnimatePresence>
        {showTeamModal && (
          <TeamModal
            teamMembers={teamMembers}
            showAddMember={showAddMember}
            setShowAddMember={setShowAddMember}
            newMember={newMember}
            setNewMember={setNewMember}
            onAddMember={handleAddTeamMember}
            onDeleteMember={handleDeleteTeamMember}
            onClose={() => setShowTeamModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Quick Create Reservation Modal - Admin only */}
      <AnimatePresence>
        {!isCollaborator && showCreateModal && (
          <QuickCreateModal
            data={showCreateModal}
            planning={planning}
            onSave={handleCreateReservation}
            onClose={() => setShowCreateModal(null)}
          />
        )}
      </AnimatePresence>

      {/* Block Slot Modal - Admin only */}
      <AnimatePresence>
        {!isCollaborator && showBlockModal && (
          <BlockSlotModal
            data={showBlockModal}
            planning={planning}
            onSave={handleBlockSlot}
            onClose={() => setShowBlockModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Week View Component
function WeekView({ days, planning, getReservationsForDay, getStatusColor, setSelectedReservation }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[900px]">
        <thead>
          <tr className="bg-slate-50">
            <th className="text-left px-4 py-3 font-semibold text-slate-700 border-b border-r border-slate-200 w-52 sticky left-0 bg-slate-50 z-10">
              Équipement
            </th>
            {days.map((day) => {
              const isToday = isSameDay(day, new Date());
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              return (
                <th 
                  key={day.toISOString()} 
                  className={`text-center px-2 py-3 font-semibold border-b border-r border-slate-200 min-w-[120px] ${
                    isToday ? 'bg-indigo-100 text-indigo-700' : isWeekend ? 'bg-slate-100' : ''
                  }`}
                >
                  <div className="text-xs uppercase tracking-wide">{format(day, 'EEEE', { locale: fr })}</div>
                  <div className="text-xl font-bold">{format(day, 'd')}</div>
                  <div className="text-xs text-slate-500">{format(day, 'MMM', { locale: fr })}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {planning.map((item, index) => {
            const equipment = item.equipment || item.product;
            return (
              <motion.tr
                key={equipment?.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className="hover:bg-slate-50/50"
              >
                <td className="px-4 py-3 border-b border-r border-slate-200 sticky left-0 bg-white z-10">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-10 rounded-full"
                      style={{ backgroundColor: equipment?.color || '#666' }}
                    />
                    <div>
                      <div className="font-semibold text-slate-900">{equipment?.name || 'Machine'}</div>
                      <div className="text-xs text-slate-500">{item.reservations?.length || 0} résa(s)</div>
                    </div>
                  </div>
                </td>
                {days.map((day) => {
                  const reservations = getReservationsForDay(equipment?.id, day);
                  const isToday = isSameDay(day, new Date());
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  
                  return (
                    <td 
                      key={day.toISOString()} 
                      className={`px-1 py-2 border-b border-r border-slate-200 align-top min-h-[80px] ${
                        isToday ? 'bg-indigo-50/50' : isWeekend ? 'bg-slate-50/50' : ''
                      }`}
                    >
                      <div className="space-y-1 min-h-[60px]">
                        {reservations.length > 0 ? reservations.map((reservation) => {
                          // Calculate display hours
                          const startTime = reservation.event_time || '09:00';
                          const startHour = parseInt(startTime.split(':')[0]);
                          const startMin = startTime.split(':')[1] || '00';
                          const duration = reservation.duration_hours || 4;
                          const endHour = startHour + duration;
                          const endTime = `${endHour}:${startMin}`;
                          
                          return (
                            <motion.div
                              key={reservation.id}
                              whileHover={{ scale: 1.02 }}
                              className={`px-2 py-1.5 rounded-lg text-white text-xs cursor-pointer shadow-sm ${getStatusColor(reservation.status)}`}
                              onClick={() => setSelectedReservation(reservation)}
                              title={`${reservation.client_name} - ${reservation.event_type} (${startTime} - ${endTime})`}
                              data-testid="week-reservation-item"
                            >
                              <div className="font-medium truncate">{reservation.client_name}</div>
                              <div className="flex items-center gap-1 text-white/90 mt-0.5">
                                <FiClock className="h-3 w-3" />
                                <span className="font-semibold">{startTime}</span>
                                <span className="opacity-70">-</span>
                                <span>{endTime}</span>
                              </div>
                              <div className="text-white/70 truncate mt-0.5">
                                {reservation.event_type}
                              </div>
                            </motion.div>
                          );
                        }) : (
                          <div className="h-full flex items-center justify-center py-4">
                            <span className="w-2 h-2 rounded-full bg-emerald-300" title="Disponible" />
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Day View Component - Vue horaire type Google Agenda
function DayView({ currentDate, planning, blockedSlots, getReservationsForDay, getStatusColor, setSelectedReservation, onCellClick, onBlockClick, selectedEquipment }) {
  const hours = WORK_HOURS;
  
  // Filter equipment if selected
  const filteredPlanning = selectedEquipment === 'all' 
    ? planning 
    : planning.filter(item => (item.equipment?.id || item.product?.id) === selectedEquipment);

  // Check if a slot is blocked
  const isSlotBlocked = (equipmentId, hour) => {
    return blockedSlots?.some(slot => 
      slot.equipment_id === equipmentId && 
      isSameDay(parseISO(slot.date), currentDate) &&
      slot.start_hour <= hour && slot.end_hour > hour
    );
  };

  // Get reservation for specific hour
  const getReservationAtHour = (equipmentId, hour) => {
    const dayReservations = getReservationsForDay(equipmentId, currentDate);
    return dayReservations.find(r => {
      const startHour = r.event_time ? parseInt(r.event_time.split(':')[0]) : 9;
      const duration = r.duration_hours || 4;
      return hour >= startHour && hour < startHour + duration;
    });
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Header with equipment names */}
        <div className="grid sticky top-0 bg-white z-10 border-b border-slate-200" 
          style={{ gridTemplateColumns: `80px repeat(${filteredPlanning.length}, minmax(150px, 1fr))` }}>
          <div className="p-3 text-sm font-semibold text-slate-600 border-r border-slate-100">
            <FiClock className="inline mr-1" />
            Heures
          </div>
          {filteredPlanning.map(item => {
            const equipment = item.equipment || item.product;
            return (
              <div key={equipment?.id} className="p-3 text-center border-r border-slate-100 last:border-r-0">
                <div className="flex items-center justify-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: equipment?.color || '#666' }} 
                  />
                  <span className="font-semibold text-slate-800">{equipment?.name}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        {hours.map(hour => (
          <div 
            key={hour} 
            className="grid border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
            style={{ gridTemplateColumns: `80px repeat(${filteredPlanning.length}, minmax(150px, 1fr))` }}
          >
            {/* Hour label */}
            <div className="p-2 text-sm font-medium text-slate-500 border-r border-slate-100 bg-slate-50/50">
              {hour === 24 ? '00' : hour}:00
            </div>
            
            {/* Equipment columns */}
            {filteredPlanning.map(item => {
              const equipment = item.equipment || item.product;
              const reservation = getReservationAtHour(equipment?.id, hour);
              const blocked = isSlotBlocked(equipment?.id, hour);
              
              return (
                <div 
                  key={equipment?.id}
                  className={`p-2 min-h-[60px] border-r border-slate-100 last:border-r-0 relative group ${
                    blocked ? 'bg-red-50' : reservation ? '' : 'hover:bg-indigo-50/50 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!reservation && !blocked) {
                      onCellClick(currentDate, hour, equipment?.id);
                    }
                  }}
                >
                  {blocked ? (
                    <div className="flex items-center justify-center h-full">
                      <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full flex items-center gap-1">
                        <FiLock className="h-3 w-3" /> Bloqué
                      </span>
                    </div>
                  ) : reservation ? (
                    <div 
                      className={`p-2 rounded-lg text-white text-xs cursor-pointer transition-transform hover:scale-[1.02] ${getStatusColor(reservation.status)}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedReservation(reservation); }}
                    >
                      <div className="font-semibold truncate">{reservation.client_name}</div>
                      <div className="opacity-80 truncate">{reservation.event_type}</div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-1">
                        <button 
                          className="p-1.5 bg-indigo-600 text-white rounded-lg text-xs flex items-center gap-1 hover:bg-indigo-700"
                          onClick={(e) => { e.stopPropagation(); onCellClick(currentDate, hour, equipment?.id); }}
                        >
                          <FiPlus className="h-3 w-3" /> Réserver
                        </button>
                        <button 
                          className="p-1.5 bg-red-500 text-white rounded-lg text-xs flex items-center gap-1 hover:bg-red-600"
                          onClick={(e) => { e.stopPropagation(); onBlockClick(currentDate, hour, equipment?.id); }}
                        >
                          <FiLock className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Month View Component
function MonthView({ days, currentDate, planning, getReservationsForDay, getStatusColor, setSelectedReservation }) {
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  
  // Group days into weeks
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // Get all reservations for a day across all equipment
  const getAllReservationsForDay = (day) => {
    let allReservations = [];
    planning.forEach(item => {
      const equipment = item.equipment || item.product;
      const dayReservations = getReservationsForDay(equipment?.id, day);
      allReservations = [...allReservations, ...dayReservations.map(r => ({
        ...r,
        equipmentName: equipment?.name,
        equipmentColor: equipment?.color
      }))];
    });
    return allReservations;
  };

  return (
    <div className="p-4">
      {/* Week day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-slate-600 py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, currentDate);
          const reservations = getAllReservationsForDay(day);
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          
          return (
            <div
              key={idx}
              className={`min-h-[100px] border rounded-lg p-2 transition-colors ${
                isToday 
                  ? 'bg-indigo-50 border-indigo-300' 
                  : isCurrentMonth 
                    ? isWeekend ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
                    : 'bg-slate-100/50 border-slate-100'
              }`}
            >
              <div className={`text-sm font-semibold mb-1 ${
                isToday 
                  ? 'text-indigo-600' 
                  : isCurrentMonth 
                    ? 'text-slate-900' 
                    : 'text-slate-400'
              }`}>
                {format(day, 'd')}
              </div>
              
              <div className="space-y-1">
                {reservations.slice(0, 3).map((reservation, rIdx) => (
                  <div
                    key={rIdx}
                    className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer text-white ${getStatusColor(reservation.status)}`}
                    onClick={() => setSelectedReservation(reservation)}
                    title={`${reservation.equipmentName} - ${reservation.client_name}`}
                  >
                    {reservation.client_name}
                  </div>
                ))}
                {reservations.length > 3 && (
                  <div className="text-xs text-slate-500 text-center">
                    +{reservations.length - 3} autres
                  </div>
                )}
                {reservations.length === 0 && isCurrentMonth && (
                  <div className="flex justify-center pt-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-300" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Equipment Legend for Month View */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <p className="text-sm font-medium text-slate-700 mb-2">Équipements :</p>
        <div className="flex flex-wrap gap-3">
          {planning.map(item => {
            const equipment = item.equipment || item.product;
            return (
              <div key={equipment?.id} className="flex items-center gap-2 text-sm">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: equipment?.color || '#666' }}
                />
                <span className="text-slate-600">{equipment?.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Reservation Modal Component
function ReservationModal({ reservation, teamMembers, getStatusColor, getStatusLabel, onAssignTeam, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">Détails de la réservation</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1 rounded-full text-white text-sm ${getStatusColor(reservation.status)}`}>
              {getStatusLabel(reservation.status)}
            </div>
            <div className="text-slate-600">{reservation.event_type}</div>
          </div>

          {reservation.equipmentName && (
            <div className="flex items-center gap-2 text-sm bg-slate-50 px-3 py-2 rounded-lg">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: reservation.equipmentColor || '#666' }}
              />
              <span className="font-medium">{reservation.equipmentName}</span>
            </div>
          )}

          <div className="grid gap-3">
            <div className="flex items-center gap-3 text-slate-700">
              <FiUser className="h-5 w-5 text-slate-400" />
              <span className="font-medium">{reservation.client_name}</span>
            </div>
            {reservation.client_email && (
              <div className="flex items-center gap-3 text-slate-600">
                <FiMail className="h-5 w-5 text-slate-400" />
                <span>{reservation.client_email}</span>
              </div>
            )}
            {reservation.client_phone && (
              <div className="flex items-center gap-3 text-slate-600">
                <FiPhone className="h-5 w-5 text-slate-400" />
                <span>{reservation.client_phone}</span>
              </div>
            )}
            {reservation.event_address && (
              <div className="flex items-center gap-3 text-slate-600">
                <FiMapPin className="h-5 w-5 text-slate-400" />
                <span>{reservation.event_address}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-slate-600">
              <FiCalendar className="h-5 w-5 text-slate-400" />
              <span>{format(parseISO(reservation.start_date), 'd MMMM yyyy', { locale: fr })}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <FiClock className="h-5 w-5 text-slate-400" />
              <span>{reservation.duration_hours} heures</span>
            </div>
          </div>

          {reservation.notes && (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-600">{reservation.notes}</p>
            </div>
          )}

          {/* Team Assignment */}
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <FiUsers className="h-4 w-4" />
              Équipe assignée
            </h4>
            {teamMembers.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun membre d'équipe. Ajoutez-en depuis le bouton "Équipe".</p>
            ) : (
              <div className="space-y-2">
                {teamMembers.map(member => {
                  const isAssigned = reservation.assigned_team?.includes(member.id);
                  return (
                    <div 
                      key={member.id}
                      className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${
                        isAssigned ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => {
                        const newAssigned = isAssigned
                          ? reservation.assigned_team.filter(id => id !== member.id)
                          : [...(reservation.assigned_team || []), member.id];
                        onAssignTeam(reservation.id, newAssigned);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: member.color || '#6366F1' }}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{member.name}</div>
                          <div className="text-xs text-slate-500">{member.role}</div>
                        </div>
                      </div>
                      {isAssigned && (
                        <FiCheck className="h-5 w-5 text-indigo-600" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="text-lg font-bold text-slate-900">
              Total: {reservation.total_price}€
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Team Modal Component
function TeamModal({ teamMembers, showAddMember, setShowAddMember, newMember, setNewMember, onAddMember, onDeleteMember, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">Gérer l'équipe</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Add Member Form */}
          {showAddMember ? (
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={newMember.name}
                  onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={e => setNewMember({ ...newMember, email: e.target.value })}
                  placeholder="jean@example.com"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newMember.phone}
                  onChange={e => setNewMember({ ...newMember, phone: e.target.value })}
                  placeholder="06 00 00 00 00"
                />
              </div>
              <div>
                <Label>Rôle</Label>
                <Input
                  value={newMember.role}
                  onChange={e => setNewMember({ ...newMember, role: e.target.value })}
                  placeholder="Technicien"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={onAddMember}>Ajouter</Button>
                <Button variant="outline" onClick={() => setShowAddMember(false)}>Annuler</Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setShowAddMember(true)} className="w-full">
              <FiPlus className="mr-2 h-4 w-4" />
              Ajouter un membre
            </Button>
          )}

          {/* Team Members List */}
          <div className="space-y-2">
            {teamMembers.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                Aucun membre d'équipe.<br />
                Ajoutez des techniciens pour les assigner aux réservations.
              </p>
            ) : (
              teamMembers.map(member => (
                <div 
                  key={member.id}
                  className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                      style={{ backgroundColor: member.color || '#6366F1' }}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-slate-500">{member.role}</div>
                      {member.phone && (
                        <div className="text-xs text-slate-400">{member.phone}</div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteMember(member.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Quick Create Reservation Modal
function QuickCreateModal({ data, planning, onSave, onClose }) {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    event_type: 'Mariage',
    event_date: format(data.date, 'yyyy-MM-dd'),
    event_time: `${data.hour}:00`,
    duration_hours: 4,
    equipment_id: data.equipmentId,
    notes: ''
  });
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isNewClient, setIsNewClient] = useState(true);

  // Fetch existing clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setClients(data.clients || data || []);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };
    fetchClients();
  }, []);

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.phone?.includes(query)
    );
  });

  // Select existing client
  const handleSelectClient = (client) => {
    setFormData({
      ...formData,
      client_name: client.name || '',
      client_email: client.email || '',
      client_phone: client.phone || ''
    });
    setSelectedClientId(client.id || client._id);
    setSearchQuery(client.name || '');
    setShowClientDropdown(false);
    setIsNewClient(false);
  };

  // Clear selection and create new client
  const handleCreateNewClient = () => {
    setFormData({
      ...formData,
      client_name: searchQuery,
      client_email: '',
      client_phone: ''
    });
    setSelectedClientId(null);
    setIsNewClient(true);
    setShowClientDropdown(false);
  };

  const equipment = planning.find(p => (p.equipment?.id || p.product?.id) === data.equipmentId);
  const equipmentName = equipment?.equipment?.name || equipment?.product?.name || 'Équipement';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_name) {
      toast.error('Le nom du client est requis');
      return;
    }
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">Nouvelle réservation</h3>
              <p className="text-white/80 text-sm">{equipmentName} - {format(data.date, 'EEEE d MMMM yyyy', { locale: fr })} à {data.hour}h</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 relative">
              <Label>Client *</Label>
              <div className="relative">
                <Input
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setShowClientDropdown(true);
                    setFormData({ ...formData, client_name: e.target.value });
                    if (selectedClientId) {
                      setSelectedClientId(null);
                      setIsNewClient(true);
                    }
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Rechercher ou créer un client..."
                  className="pr-10"
                />
                {selectedClientId && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500">
                    <FiCheck className="h-5 w-5" />
                  </div>
                )}
              </div>
              
              {/* Client Dropdown */}
              {showClientDropdown && searchQuery && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredClients.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b">
                        Clients existants
                      </div>
                      {filteredClients.slice(0, 5).map((client) => (
                        <button
                          key={client.id || client._id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="w-full px-3 py-2 text-left hover:bg-indigo-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <FiUser className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{client.name}</div>
                            <div className="text-xs text-slate-500 truncate">{client.email || client.phone}</div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={handleCreateNewClient}
                    className="w-full px-3 py-3 text-left hover:bg-green-50 flex items-center gap-3 bg-slate-50"
                  >
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <FiPlus className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-green-700">Créer un nouveau client</div>
                      <div className="text-xs text-slate-500">"{searchQuery}"</div>
                    </div>
                  </button>
                </div>
              )}
              
              {selectedClientId && (
                <p className="text-xs text-green-600 mt-1">Client existant sélectionné</p>
              )}
              {isNewClient && searchQuery && !selectedClientId && (
                <p className="text-xs text-amber-600 mt-1">Nouveau client sera créé</p>
              )}
            </div>
            <div>
              <Label>Email {isNewClient && '*'}</Label>
              <Input
                type="email"
                value={formData.client_email}
                onChange={e => setFormData({ ...formData, client_email: e.target.value })}
                placeholder="jean@example.com"
                disabled={!isNewClient && selectedClientId}
                className={!isNewClient && selectedClientId ? 'bg-slate-50' : ''}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={formData.client_phone}
                onChange={e => setFormData({ ...formData, client_phone: e.target.value })}
                placeholder="06 00 00 00 00"
                disabled={!isNewClient && selectedClientId}
                className={!isNewClient && selectedClientId ? 'bg-slate-50' : ''}
              />
            </div>
          </div>

          <div>
            <Label>Type d'événement</Label>
            <select
              value={formData.event_type}
              onChange={e => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-md"
            >
              <option value="Mariage">Mariage</option>
              <option value="Anniversaire">Anniversaire</option>
              <option value="Entreprise">Événement d'entreprise</option>
              <option value="Baptême">Baptême</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Heure de début</Label>
              <Input
                type="time"
                value={formData.event_time}
                onChange={e => setFormData({ ...formData, event_time: e.target.value })}
              />
            </div>
            <div>
              <Label>Durée (heures)</Label>
              <select
                value={formData.duration_hours}
                onChange={e => setFormData({ ...formData, duration_hours: parseInt(e.target.value) })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md"
              >
                <option value="2">2 heures</option>
                <option value="3">3 heures</option>
                <option value="4">4 heures</option>
                <option value="5">5 heures</option>
                <option value="6">6 heures</option>
                <option value="8">8 heures</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Informations supplémentaires..."
              className="w-full px-3 py-2 border border-slate-200 rounded-md h-20 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1">
              <FiCheck className="mr-2 h-4 w-4" />
              Créer la réservation
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Block Slot Modal
function BlockSlotModal({ data, planning, onSave, onClose }) {
  const [formData, setFormData] = useState({
    date: format(data.date, 'yyyy-MM-dd'),
    start_hour: data.hour,
    end_hour: data.hour + 4,
    equipment_id: data.equipmentId,
    reason: ''
  });

  const equipment = planning.find(p => (p.equipment?.id || p.product?.id) === data.equipmentId);
  const equipmentName = equipment?.equipment?.name || equipment?.product?.name || 'Équipement';

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-red-500 to-orange-500 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FiLock /> Bloquer un créneau
              </h3>
              <p className="text-white/80 text-sm">{equipmentName} - {format(data.date, 'EEEE d MMMM', { locale: fr })}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Heure de début</Label>
              <select
                value={formData.start_hour}
                onChange={e => setFormData({ ...formData, start_hour: parseInt(e.target.value) })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md"
              >
                {WORK_HOURS.map(h => (
                  <option key={h} value={h}>{h === 24 ? '00' : h}:00</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Heure de fin</Label>
              <select
                value={formData.end_hour}
                onChange={e => setFormData({ ...formData, end_hour: parseInt(e.target.value) })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md"
              >
                {WORK_HOURS.filter(h => h > formData.start_hour).map(h => (
                  <option key={h} value={h}>{h}:00</option>
                ))}
                <option value={21}>21:00</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Raison (optionnel)</Label>
            <Input
              value={formData.reason}
              onChange={e => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Ex: Maintenance, congés..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="destructive" className="flex-1">
              <FiLock className="mr-2 h-4 w-4" />
              Bloquer ce créneau
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
